import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { User, Mail, Lock, Loader2, Eye, EyeOff } from "lucide-react";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_procurement-hub-215/artifacts/5wtlks8a_Somcore%20-%20Logo.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = "Sign In · Somcore Operations Hub"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-[#F1F3F6]">
      {/* Left decorative panel */}
      <div className="relative hidden overflow-hidden bg-[#122E5C] lg:flex lg:flex-col lg:items-center lg:justify-center">
        <div className="pointer-events-none absolute -top-20 right-10 h-72 w-72 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute top-1/3 -right-16 h-40 w-40 rounded-full bg-white/5" />
        <div className="pointer-events-none absolute -bottom-24 -left-16 h-80 w-80 rounded-full bg-white/[0.04]" />
        <div className="pointer-events-none absolute bottom-24 left-24 h-24 w-24 rounded-full bg-white/[0.04]" />
        <div className="relative z-10 flex flex-col items-center px-12 text-center text-white">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20">
            <User className="h-9 w-9 text-white" />
          </div>
          <h1 className="mt-8 font-heading text-4xl font-bold tracking-tight">Welcome Back</h1>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-300">
            Sign in to the Somcore Operations Hub and pick up where you left off.
          </p>
          <img src="/somcore-logo-white.png" alt="Somcore" className="mt-12 ml-8 h-20 w-auto opacity-95" />
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-[0_10px_40px_-15px_rgba(18,46,92,0.25)]">
          <div className="flex flex-col items-center text-center">
            <img src={LOGO} alt="Somcore" className="h-10 w-auto" />
            <div className="mt-6 text-xs font-semibold tracking-wide text-primary">Sign In</div>
            <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-slate-900">Welcome Back</h2>
            <p className="mt-1 text-sm text-muted-foreground">Enter your credentials to access your workspace</p>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <label htmlFor="email" className="flex items-center gap-1 text-xs font-semibold tracking-wide text-slate-600">
                <span className="text-primary">*</span> Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required placeholder="you@example.com" className="h-11 pl-10" data-testid="login-email" />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="flex items-center gap-1 text-xs font-semibold tracking-wide text-slate-600">
                <span className="text-primary">*</span> Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  required placeholder="Your password" className="h-11 pl-10 pr-10" data-testid="login-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                  aria-label={showPw ? "Hide password" : "Show password"} data-testid="toggle-password"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}

            <div className="text-right">
              <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline" data-testid="forgot-password-link">
                Forgot Password?
              </Link>
            </div>

            <Button type="submit" className="h-11 w-full text-base" disabled={loading} data-testid="login-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign In
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">At the Core of Global Resources.</p>
        </div>
      </div>
    </div>
  );
}
